import { useEffect, useMemo, useState } from 'react';
import type {
  LoginResponse,
  OAuthAuthorizeCheck,
  OAuthAuthorizeParams,
} from '@nycelogic/nycebuddy-contracts';
import { BuddyApiError } from '@nycelogic/nycebuddy-api-client';
import { Button } from '@nycelogic/nycebuddy-chat-ui/ui';
import { useTranslation } from '@nycelogic/nycebuddy-chat-ui/i18n';
import { assets, ThemeRoot } from '@nycelogic/nycebuddy-chat-ui';
import { client } from '../auth/session';
import { Login } from './Login';

const PARAMS = [
  'response_type',
  'client_id',
  'redirect_uri',
  'code_challenge',
  'code_challenge_method',
  'state',
  'scope',
  'resource',
] as const;

type State =
  | { kind: 'checking' }
  | { kind: 'invalid'; reason: string }
  | { kind: 'login'; check: OAuthAuthorizeCheck }
  | { kind: 'consent'; check: OAuthAuthorizeCheck; login: LoginResponse }
  | { kind: 'disabled'; check: OAuthAuthorizeCheck; system: string }
  | { kind: 'redirecting'; check: OAuthAuthorizeCheck }
  | { kind: 'failed'; reason: string };

/** The server's `redirect` on a 400: the error goes back to the client instead of being shown here. */
function redirectOf(e: unknown): string | undefined {
  return e instanceof BuddyApiError ? e.body?.redirect : undefined;
}

function reasonOf(e: unknown): string {
  if (e instanceof BuddyApiError) return e.body?.error_description || e.message;
  return e instanceof Error ? e.message : String(e);
}

/**
 * `/authorize?…` — the consent page of the OAuth flow for `/mcp` (ADR-0016), reached from an MCP client such as a
 * claude.ai connector. It signs the user in afresh with the usual picker (the result is never stored as this tab's
 * session), asks for consent and hands that sign-in to the server, which answers with the client's redirect carrying a
 * one-time code. The client then takes the sign-in over at `/oauth/token`; nothing of it stays in the browser.
 */
export function Authorize() {
  const { t } = useTranslation();
  const params = useMemo<OAuthAuthorizeParams>(() => {
    const q = new URLSearchParams(window.location.search);
    const p: Record<string, string> = {};
    for (const k of PARAMS) {
      const v = q.get(k);
      if (v !== null) p[k] = v;
    }
    return p;
  }, []);
  const [state, setState] = useState<State>({ kind: 'checking' });

  useEffect(() => {
    let alive = true;
    client.oauth
      .check(params)
      .then((check) => alive && setState({ kind: 'login', check }))
      .catch((e: unknown) => {
        if (!alive) return;
        const back = redirectOf(e);
        if (back) window.location.replace(back);
        else setState({ kind: 'invalid', reason: reasonOf(e) });
      });
    return () => {
      alive = false;
    };
  }, [params]);

  const clientName = (check: OAuthAuthorizeCheck) => check.clientName || t('oauth.anApplication');

  const finish = async (check: OAuthAuthorizeCheck, login: LoginResponse | null, deny: boolean) => {
    setState({ kind: 'redirecting', check });
    try {
      const { redirect } = await client.oauth.approve(
        deny ? { ...params, deny: true } : { ...params, refresh_token: login?.refresh_token },
        deny ? undefined : login?.access_token
      );
      window.location.replace(redirect);
    } catch (e) {
      if (e instanceof BuddyApiError && e.code === 'feature_disabled')
        setState({ kind: 'disabled', check, system: login?.systemName || login?.system || '' });
      else setState({ kind: 'failed', reason: reasonOf(e) });
    }
  };

  // The sign-in already says whether the system allows MCP: say so before asking for consent, and let the user pick
  // another system instead of ending on an error. The server checks again at approve.
  const signedIn = (check: OAuthAuthorizeCheck, login: LoginResponse) =>
    setState(
      login.features?.mcp === false
        ? { kind: 'disabled', check, system: login.systemName || login.system }
        : { kind: 'consent', check, login }
    );

  if (state.kind === 'login') {
    return (
      <div className="app">
        <div className="nb-oauth-stack">
          <Login
            systemsPath="/"
            prompt={t('oauth.signInPrompt', { client: clientName(state.check) })}
            onLogin={() => {}}
            onSignedIn={(login) => signedIn(state.check, login)}
          />
          <div className="nb-oauth-cancel">
            <Button variant="secondary" onClick={() => void finish(state.check, null, true)}>
              {t('oauth.cancel')}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (state.kind === 'disabled') {
    const { check, system } = state;
    return (
      <div className="app">
        <ThemeRoot theme="light" style={{ background: 'transparent' }}>
          <div className="nb-login nb-oauth">
            <header className="nb-header">
              <img src={assets.logoBlack} alt="nyceBuddy" style={{ width: 200 }} />
            </header>
            <div className="nb-login-form">
              <p className="nb-oauth-text" role="alert">
                {t('oauth.mcpDisabled', { system })}
              </p>
              <div className="nb-oauth-actions">
                <Button variant="secondary" onClick={() => void finish(check, null, true)}>
                  {t('oauth.cancel')}
                </Button>
                <Button onClick={() => setState({ kind: 'login', check })}>{t('oauth.otherSystem')}</Button>
              </div>
            </div>
          </div>
        </ThemeRoot>
      </div>
    );
  }

  if (state.kind === 'consent') {
    const { check, login } = state;
    const name = clientName(check);
    return (
      <div className="app">
        <ThemeRoot theme="light" style={{ background: 'transparent' }}>
          <div className="nb-login nb-oauth">
            <header className="nb-header">
              <img src={assets.logoBlack} alt="nyceBuddy" style={{ width: 200 }} />
            </header>
            <div className="nb-login-form">
              <h1 className="nb-oauth-title">{t('oauth.consentTitle', { client: name })}</h1>
              <p className="nb-oauth-text">
                {t('oauth.consentBody', {
                  user: login.user?.name || login.user?.code || '',
                  system: login.systemName || login.system,
                })}
              </p>
              <p className="nb-oauth-text">{t('oauth.consentLifetime', { client: name })}</p>
              <p className="nb-oauth-note">{t('oauth.redirectNote', { host: check.redirectHost })}</p>
              <div className="nb-oauth-actions">
                <Button variant="secondary" onClick={() => void finish(check, login, true)}>
                  {t('oauth.deny')}
                </Button>
                <Button onClick={() => void finish(check, login, false)}>{t('oauth.allow')}</Button>
              </div>
            </div>
          </div>
        </ThemeRoot>
      </div>
    );
  }

  const text =
    state.kind === 'checking'
      ? t('oauth.checking')
      : state.kind === 'redirecting'
        ? t('oauth.redirecting', { client: clientName(state.check) })
        : state.kind === 'invalid'
          ? t('oauth.invalid', { reason: state.reason })
          : t('oauth.failed', { reason: state.reason });
  return (
    <div className="app">
      <div className="app-center" role="status">
        <p>{text}</p>
      </div>
    </div>
  );
}
